import { useState, useCallback, useEffect } from "react";
import positionService from "../services/positionService";
import { SetAuditTrail, SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";
import { useGeneralMessage } from "../../common/APICommon";
import { FEATURES } from "../../config/features";

export const usePosition = ({ isMainModule = true } = {}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [activeData, setActiveData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await positionService.getAll();
      const responseData = res.data || [];
      setData(responseData);
      setFilterData(responseData);
    } catch (err) {
      console.error("Failed to fetch positions", err);
      showGeneralMessage("Failed to load positions", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  const fetchActivePositions = useCallback(async (departmentId = null) => {
    setLoading(true);
    try {
      const res = await positionService.getAllActive(departmentId);
      const responseData = res.data || [];
      setActiveData(responseData);
    } catch (err) {
      console.error("Failed to fetch positions", err);
      showGeneralMessage("Failed to load positions", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  const fetchById = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await positionService.getById(id);
      const responseData = res.data || [];
      setSelectedData(responseData);

      console.log({responseData});
    } catch (err) {
      console.error("Failed to fetch positions", err);
      showGeneralMessage("Failed to load positions", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    if(!isMainModule) return;
    fetchPositions();
  }, [fetchPositions, isMainModule]);

  const handleAction = async (actionFn, auditData, ...args) => {
    setLoading(true);
    try {
      const res = await actionFn(...args);

      if (Array.isArray(auditData) && auditData.length > 0) {
        await SetAuditTrailBulk({ data: auditData });
      } else if (auditData && !Array.isArray(auditData)) {
        await SetAuditTrail(auditData);
      }

      showGeneralMessage(res.data?.message || "Action successful", true);
      await fetchPositions();
      return { success: true, data: res.data };
    } catch (err) {
      const errorMsg = err.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMsg, false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const searchData = (value) => {
    if (!value) {
      setData(filterData);
      return;
    }
    const lower = value.toLowerCase();
    setData(filterData.filter(f => {
      const matchDesc = f.description?.toLowerCase().includes(lower);
      const matchId = f.id?.toString().includes(lower);
      const matchUnit = FEATURES.POSITION_UNIT_CODE && f.unitCode?.toLowerCase().includes(lower);
      const matchCode = FEATURES.POSITION_CODE && f.code?.toLowerCase().includes(lower);
      return matchDesc || matchId || matchUnit || matchCode;
    }));
  };

  return {
    loading,
    data,
    selectedData,
    activeData, setActiveData,
    showMessage,
    message,
    isSuccess,
    searchData,
    createPosition: (p, audit) => handleAction(positionService.create, audit, p),
    updatePosition: (id, p, audit) => handleAction(positionService.update, audit, id, p),
    refresh: fetchPositions,
    fetchActivePositions,
    fetchById
  };
};